import { useEffect, useRef } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useScrollReveal } from "../components/useScrollReveal";
import { useSEO } from "../components/useSEO";
import "./WhyModus.css";

export default function WhyModus() {
  useScrollReveal();
  useSEO(
    "Why Modus | Measuring Whether Patients Actually Get Better",
    "Modus measures whether patients get better after care, not just billing codes, analyzing 5,400+ real codes per doctor against Medicare data to score outcomes."
  );
  const heroSectionRef = useRef(null);

  // Same background depth parallax as Platform's hero glow — this one
  // wraps the existing glow blob (which already has its own idle
  // "float" CSS animation) in an outer element instead of touching it
  // directly, so the scroll-driven transform and the existing idle
  // animation apply independently without fighting over the same
  // element's transform property.
  useEffect(() => {
    const wrapper = document.getElementById("wmHeroGlowParallax");
    const section = heroSectionRef.current;
    if (!wrapper || !section) return;
    if (!window.matchMedia("(prefers-reduced-motion: no-preference)").matches) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
      wrapper.style.transform = `translateY(${rect.top * 0.15}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <SiteHeader active="why-modus" />



<section className="wm-hero" ref={heroSectionRef}>
  <div className="hero-glow-parallax" id="wmHeroGlowParallax">
    <div className="hero-glow-blob-floating" />
  </div>
  <div className="wrap wm-hero-grid">
    <div className="wm-hero-copy">
      <span className="kicker">Why Modus</span>
      <h1>Are patients better or worse off <span className="accent">after a doctor's care?</span></h1>
      <p>It's the only question that matters when you choose a physician, and almost nobody measures it. We built a company around answering it.</p>
      <div className="hero-pillars">
        <div className="hp"><span className="dot"></span>Independent, no affiliations</div>
        <div className="hp"><span className="dot"></span>183M-record methodology</div>
        <div className="hp"><span className="dot"></span>Physician-founded &amp; led</div>
        <div className="hp"><span className="dot"></span>First on CMS's NPD</div>
      </div>
    </div>
    <div className="wm-hero-visual">
      <div className="phv-frame">
        <div className="phv-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.6" y2="16.6"></line></svg>
          <span>Why Modus</span>
          <span className="phv-live"><span className="dot"></span>Live</span>
        </div>
        <div className="phv-stack">
          <div className="phv-layer" style={{"--i": "0"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></span><div><div className="phv-t">Independent</div><div className="phv-s">No hospital or insurer affiliation</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "1"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg></span><div><div className="phv-t">183M Records</div><div className="phv-s">Published, transparent methodology</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "2"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></span><div><div className="phv-t">Physician-Founded</div><div className="phv-s">Led by a doctor who still practices</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "3"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 8 4 8"/></svg></span><div><div className="phv-t">First on CMS's NPD</div><div className="phv-s">Built on the new National Provider Directory</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
        </div>
        <div className="phv-more">Independent · Transparent · Physician-led</div>
      </div>
    </div>
  </div>
</section>


<section>
  <div className="wrap">
    <div className="events-band reveal">
      <p className="big">We measure events that happened to patients after a doctor's care. Everyone else measures codes, and codes can be written to say anything.</p>
      <p className="small">An event either happened or it didn't. A patient went back into surgery, spent eleven days in the hospital, didn't survive the year. None of it can be upcoded into existence.</p>
    </div>
  </div>
</section>

<section style={{background: "var(--surface)"}}>
  <div className="wrap">
    <span className="step-tag reveal">The problem</span>
    <h2 className="wm-title reveal">We pay for codes. <span className="accent-teal">Not for whether it worked.</span></h2>
    <p className="wm-lead reveal">Two things are wrong with how physicians get paid, and both point to the same fix.</p>
    <div className="twocol cards reveal">
      <div>
        <div className="tc-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18M7 15h4"/></svg></div>
        <h3>The unit is wrong</h3>
        <p>Reimbursement is built on ICD and CPT codes. They record what was done and how thoroughly it was written down. Whether the patient's life improved was never an input to the payment, so it was never an input to the behavior.</p>
      </div>
      <div>
        <div className="tc-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3m0 12v3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1M3 12h3m12 0h3M5.6 18.4l2.1-2.1m8.6-8.6 2.1-2.1"/></svg></div>
        <h3>The assumption is wrong</h3>
        <p>The system assumed physicians would restrain themselves against their own financial interest. Many do. Some don't. No counteracting check was ever built, because none was thought necessary.</p>
      </div>
    </div>
    <div className="pull-quote reveal">
      <div className="quote-mark-grid">
        <div className="quote-mark" aria-hidden="true">&#8220;</div>
        <div>
          <blockquote style={{maxWidth: "none"}}>If men were angels, no government would be necessary.</blockquote>
          <cite>James Madison, Federalist No. 51, 1788</cite>
        </div>
      </div>
      <p className="quote-close">Madison's point wasn't that men are wicked. It was that a system which depends on their virtue has no design at all. Doctors are human. A reimbursement architecture that assumes otherwise isn't a moral failure of physicians, it's an engineering failure of the architecture.</p>
      <p className="quote-close"><strong>So we don't add another layer of supervision.</strong> We find the physicians whose internal check already works, and we keep measuring whether patients got better.</p>
    </div>
  </div>
</section>

<section>
  <div className="wrap">
    <span className="step-tag reveal">At a glance</span>
    <h2 className="wm-title reveal">Reasons teams trust <span className="accent-teal">Modus</span> with doctor decision.</h2>
    <div className="glance-grid reveal-stagger">
      <div className="glance-item">
        <div className="glance-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/><polyline points="9 12 11.5 14.5 15.5 10"/></svg></div>
        <h3>Clinical Leadership</h3>
        <p>Our data and software team is led by our clinician founder, who has over two decades of clinical experience and is still practicing medicine today.</p>
      </div>
      <div className="glance-item">
        <div className="glance-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/><circle cx="12" cy="12" r="0.8" fill="currentColor"/></svg></div>
        <h3>Highest Accuracy</h3>
        <p>We analyze, on average, 5,400+ real billing codes per doctor, verified against Medicare and refreshed yearly, using National Provider Identifier (NPI) level data down to the individual physician.</p>
      </div>
      <div className="glance-item">
        <div className="glance-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6a1 1 0 0 1 1 1v1h2a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h2V4a1 1 0 0 1 1-1Z"/><path d="M9 11h6M9 15h6"/></svg></div>
        <h3>Transparent Methodology</h3>
        <p>Every quality and cost metric ships with a published numerator and denominator, each traceable back to the CMS billed codes it was built from. Nothing about how a score was built is hidden.</p>
      </div>
      <div className="glance-item">
        <div className="glance-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M5 8l-3 8a4 4 0 0 0 6 0l-3-8Zm14 0l-3 8a4 4 0 0 0 6 0l-3-8ZM5 8h14M8 3h8"/></svg></div>
        <h3>Independent</h3>
        <p>We have no affiliation with hospitals or large insurers, and nothing to gain by rating a doctor higher or lower than the data says.</p>
      </div>
      <div className="glance-item">
        <div className="glance-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z"/></svg></div>
        <h3>Simplicity</h3>
        <p>We built an industry-first capability: distilling a clear signal from the noise of massive claims data into one simple, actionable doctor performance rating.</p>
      </div>
    </div>
  </div>
</section>

<section>
  <div className="wrap">
    <span className="step-tag reveal">Modus Clinical Intelligence</span>
    <h2 className="wm-title reveal">It took five years of practice to learn <span className="accent-teal">which doctors I could trust.</span></h2>
    <p className="wm-lead reveal">And that knowledge only covered a thirty-minute drive.</p>
    <p className="reveal" style={{fontSize: "14px", color: "var(--ink-2)", lineHeight: "1.75", maxWidth: "780px"}}>Every physiatrist has that private list of who to trust. It takes years to build, and it dies at the edge of your referral area.</p>

    <div className="compare-flow reveal">
      <div className="flow-step old">
        <div className="flow-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg></div>
        <b>5 years of watching</b>
        <span>Covers one referral area, never written down</span>
      </div>
      <div className="flow-arrow">→</div>
      <div className="flow-step new">
        <div className="flow-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z"/></svg></div>
        <b>Modus Clinical Intelligence</b>
        <span>The same judgment, instantly, in any state</span>
      </div>
    </div>

    <div className="highlight-list reveal">
      <div className="highlight-item">
        <div className="hi-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.6" y2="16.6"/></svg></div>
        <p><strong>We use the Medicare population, not Medicare's quality metrics.</strong> The claims show what happened to patients. CMS's own scores show what a practice chose to report, a different thing entirely.</p>
      </div>
    </div>

    <div className="scope-band reveal">
      <div className="scope-cell">
        <div className="scope-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/></svg></div>
        <h3>Elective and expensive procedures only</h3>
        <p>Musculoskeletal and cardiology, where the decision to operate is itself the judgment being measured. Emergency and trauma are excluded entirely, so no one can blame their numbers on the cases that walked through the door.</p>
      </div>
      <div className="scope-cell">
        <div className="scope-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.5-7 8-7s8 3 8 7"/></svg></div>
        <h3>Peer-matched, same specialty and same state</h3>
        <p>An orthopedic surgeon is never measured against neurosurgeons, and a surgeon in Missouri is never measured against one in Manhattan. Everyone in the pool faces the same patients, the same market, the same rules.</p>
      </div>
    </div>

    <div style={{marginTop: "44px"}}>
      <h3 className="subhead">What we read: six signals of judgment</h3>
      <p className="subhead-lead">Three describe how readily a physician reaches for intervention. Three test the answer every expensive surgeon gives, that they take the hardest cases.</p>
    </div>
    <div className="signal-groups reveal">
      <div className="signal-group behavior">
        <div className="signal-group-head">
          <span className="signal-group-badge">Behavior signals</span>
          <span className="signal-group-title">How readily this physician reaches for intervention</span>
        </div>
        <div className="signal-grid-inner">
          <div className="signal-card">
            <div className="si-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12h16M4 6h10M4 18h7"/></svg></div>
            <h4>Rate of surgery, new and existing patients</h4>
            <p>Cost efficiency. How readily this physician reaches for the operating room.</p>
          </div>
          <div className="signal-card">
            <div className="si-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v6M12 22v-4M4.9 4.9l4.2 4.2M15 15l4.2 4.2M2 12h6M22 12h-4"/><circle cx="12" cy="12" r="2.5"/></svg></div>
            <h4>Short- and long-acting opioid prescribing</h4>
            <p>Disability potential, and the earliest signal of a claim that won't close.</p>
          </div>
          <div className="signal-card">
            <div className="si-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6l1 4H8l1-4Z"/><path d="M6 7h12l-1 13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 7Z"/><path d="M10 12h4"/></svg></div>
            <h4>Use of expensive in-office testing</h4>
            <p>Cost management. Testing owned by the person ordering it.</p>
          </div>
        </div>
      </div>
      <div className="signal-group outcome">
        <div className="signal-group-head">
          <span className="signal-group-badge">Outcome signals</span>
          <span className="signal-group-title">Whether they actually take the hardest cases</span>
        </div>
        <div className="signal-grid-inner">
          <div className="signal-card">
            <div className="si-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="10" rx="2"/><path d="M9 10V7a3 3 0 0 1 6 0v3"/></svg></div>
            <h4>Postoperative hospital days</h4>
            <p>Severity of complications after elective surgery. A day versus a month.</p>
          </div>
          <div className="signal-card">
            <div className="si-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 12h4M3 12l3-3M3 12l3 3"/><path d="M12 8v4l3 2"/></svg></div>
            <h4>Revision surgery on the same body part</h4>
            <p>Failure rate, cost, and disability. The operation that had to be done twice.</p>
          </div>
          <div className="signal-card">
            <div className="si-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9Z"/></svg></div>
            <h4>One-year postoperative mortality</h4>
            <p>Clinical judgment in elective cases on high-risk patients. Whether to cut at all.</p>
          </div>
        </div>
      </div>
    </div>
    <p style={{marginTop: "24px", fontSize: "13.5px", color: "var(--ink-2)", lineHeight: "1.7", maxWidth: "780px"}}>If a physician genuinely takes the hardest cases, that shows up as strong outcomes on hard cases. This is also why we don't risk-adjust. Risk adjustment would grant the defense in advance instead of testing it.</p>

    <div className="callout-box reveal">
      <div className="callout-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/><polyline points="9 12 11.5 14.5 15.5 10"/></svg></div>
      <div>
        <h4>Then a third layer: background check and red flags</h4>
        <p>Once the clinical signals have done their work, an AI agent checks name, address, NPI, and license against federal and state sources for disciplinary actions, sanctions, and litigation history. It sits beside the score rather than inside it, because it's a different kind of fact. A license action either exists or it doesn't. It's not measured against peers.</p>
      </div>
    </div>
  </div>
</section>

<section style={{background: "var(--surface)"}}>
  <div className="wrap">
    <span className="step-tag reveal">Not just a tool, a science</span>
    <h2 className="wm-title reveal">Every score is backed by <span className="accent-teal">verifiable billing activity in CMS data.</span></h2>
    <p className="wm-lead reveal">A provider lookup tool is only as good as what's under it. Ours rests on what physicians actually billed, recorded by CMS, read through a clinical methodology built by a practicing physician and validated by an independent third party.</p>
    <div className="sci-band reveal">
      <div className="sci-grid">
        <div className="sci-cell"><div className="sci-num">183M+</div><div className="sci-lbl">historical patient records analyzed</div></div>
        <div className="sci-cell"><div className="sci-num">5,400+</div><div className="sci-lbl">billing codes per doctor, refreshed yearly</div></div>
        <div className="sci-cell"><div className="sci-num">$100K</div><div className="sci-lbl">Validation Institute credibility guarantee</div></div>
        <div className="sci-cell"><div className="sci-num">20+</div><div className="sci-lbl">specialties covered today, more coming</div></div>
      </div>
      <div className="score-flow twocol-below">
        <div className="sf-step">
          <div className="sf-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6a1 1 0 0 1 1 1v1h2a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h2V4a1 1 0 0 1 1-1Z"/></svg></div>
          <b>CMS billed codes</b>
          <span>Objective, fixed, already public</span>
        </div>
        <div className="sf-arrow">→</div>
        <div className="sf-step">
          <div className="sf-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z"/></svg></div>
          <b>Modus judgment layer</b>
          <span>Which events matter, how they're weighted</span>
        </div>
        <div className="sf-arrow">→</div>
        <div className="sf-step highlight">
          <div className="sf-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/><polyline points="9 12 11.5 14.5 15.5 10"/></svg></div>
          <b>The Modus Score</b>
          <span>Published, reproducible, contestable by any physician</span>
        </div>
      </div>
    </div>

    <div className="pull-quote reveal" style={{marginTop: "32px"}}>
      <blockquote style={{maxWidth: "none"}}>Our data is backed by the toughest population there is.</blockquote>
      <div className="quote-traits-row">
        <div className="quote-trait">
          <div className="qt-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"/><path d="M8 12h2.5l1.5-3 2 6 1.5-3H18"/></svg></div>
          <div className="qt-text"><b>Sicker</b><span>Higher clinical acuity per patient</span></div>
        </div>
        <div className="quote-trait">
          <div className="qt-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.5a2.5 2.5 0 0 1 2.5-1.5h.5a2 2 0 0 1 0 4h-1a2 2 0 0 0 0 4h.5a2.5 2.5 0 0 0 2.5-1.5"/></svg></div>
          <div className="qt-text"><b>Poorer</b><span>Lower-income patient population</span></div>
        </div>
        <div className="quote-trait">
          <div className="qt-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg></div>
          <div className="qt-text"><b>Older</b><span>Higher average age than commercial</span></div>
        </div>
      </div>
      <p className="quote-close" style={{maxWidth: "none"}}>A doctor's judgment doesn't change with the type of payer sitting across from them. A physician who operates too readily on a Medicare patient operates too readily, period. That's what makes the signal transferable to any population you bring to it.</p>
    </div>
  </div>
</section>

<section style={{background: "var(--surface)"}}>
  <div className="wrap">
    <span className="step-tag reveal">Outcomes, quantified</span>
    <h2 className="wm-title reveal">The gap between a high-rated and a low-rated doctor <span className="accent-teal">isn't small.</span></h2>
    <p className="wm-lead reveal">Modus-rated orthopedic surgeons, top 8% vs. bottom 8% on the Modus Score, same specialty, same geography, same patient mix.</p>

    <div className="outcomes-band reveal">
      <div className="outcomes-head">
        Outcomes of <span className="outcomes-pill top">TOP 8%</span> Vs. <span className="outcomes-pill bottom">BOTTOM 8%</span>
      </div>
      <div className="outcomes-sub">On average results in reduction of:</div>
      <div className="outcomes-grid">
        <div className="outcomes-item"><div className="outcomes-lbl">1 Year Mortality Rate</div><div className="outcomes-val">37%</div></div>
        <div className="outcomes-item"><div className="outcomes-lbl">Repeated Surgeries</div><div className="outcomes-val">61%</div></div>
        <div className="outcomes-item"><div className="outcomes-lbl">Disability *LA Opioid</div><div className="outcomes-val">72%</div></div>
        <div className="outcomes-item"><div className="outcomes-lbl">Total Cost Per Doctor</div><div className="outcomes-val">63%</div></div>
        <div className="outcomes-item"><div className="outcomes-lbl">Hospital Days</div><div className="outcomes-val">37%</div></div>
        <div className="outcomes-item"><div className="outcomes-lbl">All Opioids Prescriptions</div><div className="outcomes-val">13%</div></div>
        <div className="outcomes-item"><div className="outcomes-lbl">Total Surgeries</div><div className="outcomes-val">94%</div></div>
      </div>
      <div className="outcomes-note">*LA Opioid = long-acting opioid prescriptions. Source: Modus Clinical Intelligence, 183M+ historical patient records.</div>
    </div>
  </div>
</section>


<section>
  <div className="wrap">
    <div className="cta-band reveal">
      <h2>See what the data says about <span className="accent-light">your own network.</span></h2>
      <p>Bring a real panel. We'll show you what's flagged, what's stale, and what's actually good.</p>
      <button className="btn btn-white btn-lg" onClick={() => (window.location.href = "/about#contact")}>Book a Demo</button>
    </div>
  </div>
</section>


      <SiteFooter />
    </>
  );
}